import React, { useState, useEffect } from 'react';
import { Review, Page, ReviewModel } from '../../typings/reviews';
import ReviewExtendedCard from '../../page/all-reviews/review-extented-card';
import { useFeatureServices } from '../../services/use-feature-services';
import {
  FetchFeedbackReviews,
  fetchFeedbackReviews,
} from '../../fetch-data/fetch-feedback-reviews';
import { ReviewsContainer } from './styled';
import { LoaderSpinner } from '@vw-marketing/us-components';
import PageBar from '../page-bar';
import NoReviews from '../no-reviews';
import {
  useUpdateReviewModel,
  useReviewModel,
} from '../../services/reviews-data-service';
import { FeatureAppTrackingManager } from '../../services/use-tracking-manager';
import {
  onHandleCollapseTextEventTracking,
  onHandleReviewsReloadTrackingEvent,
} from '../../utils/tracking-helpers';
import { AppConstants } from '../../utils/app-constants';
import { parseReviewsData, validatePage } from './helpers';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';

interface ReviewsHandlerProps {
  pageNumber: number;
  readonly reviewsModel?: ReviewModel;
  readonly pageData?: Page;
  readonly modelName: string;
  readonly reverse?: boolean;
  readonly rating?: string;
  readonly trackingManager?: FeatureAppTrackingManager;
  setPageData: (page: Page) => void;
  hidePageData: (status: boolean) => void;
  handleResetFilters: () => void;
  handlePageChange: (number: number) => void;
}

const ReviewsHandler: React.FC<ReviewsHandlerProps> = props => {
  const sliceOffset = AppConstants.ALL_REVIEWS_SIZE;
  const {
    reviewsModel,
    pageNumber,
    modelName,
    rating,
    reverse,
    setPageData,
    pageData,
    handlePageChange,
    handleResetFilters,
    trackingManager,
    hidePageData,
  } = props;
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [currentPage, setCurrentPage] = useState<ReviewModel | undefined>(
    undefined,
  );
  const [nextPage, setNextPage] = useState<ReviewModel | undefined>(undefined);
  const [previousPage, setPreviousPage] = useState<ReviewModel | undefined>(
    undefined,
  );

  const updateState = useUpdateReviewModel();
  const state = useReviewModel();
  const { apiKeyContent } = useFeatureAppConfig();

  // Feedback request configuration
  const { 'service-config-provider': serviceProvider } = useFeatureServices();
  const reviewsHostData = serviceProvider.configs['fiveStars'];
  /**
   * Auxiliar function for app status
   * @param status
   */
  const updateReviewsStatus = (status: boolean) => {
    hidePageData(status);
    setIsLoadingReviews(status);
  };

  /**
   * Auxiliar function for app status
   * @param status
   */
  const clearReviewsData = (all?: boolean) => {
    setCurrentPage(undefined);
    !all && setNextPage(undefined);
    !all && setPreviousPage(undefined);
  };

  /**
   * Get reviews from api
   * @param pageNumber
   * @param resetReviews
   * @param secondPageNumber
   */
  const requestNewPage = (
    pageNumber: number,
    isBackgroundProcess: boolean,
    updateReviews: (reviews: ReviewModel | undefined) => void,
  ) => {
    let requestData: FetchFeedbackReviews = {
      reviewsHostData,
      dataVersion: reviewsModel?.dataVersion || '',
      apiKeyContent,
      model: modelName,
      size: sliceOffset as number,
      page: pageNumber,
      rating: rating !== 'all' ? rating : undefined,
    };

    if (!isBackgroundProcess && isLoadingReviews) return;
    !isBackgroundProcess && updateReviewsStatus(true);

    return fetchFeedbackReviews(requestData)
      .then((reviews: ReviewModel) => {
        let reviewsResponse: ReviewModel | undefined = undefined;
        if (reviews) {
          // Update context with first page state
          reviewsResponse = { ...reviews };
          if (!rating || rating === 'all') {
            !reverse && requestData.page === 1
              ? updateState && state && updateState({ ...state, ...reviews })
              : updateState &&
                state &&
                updateState({
                  ...state,
                  page: reviews.page,
                });
          }
        }
        !isBackgroundProcess &&
          reviewsResponse &&
          setPageData(reviewsResponse.page);
        updateReviews(parseReviewsData(reviewsResponse, reverse));
      })
      .catch(error => {
        console.log(`Error FiveStars: ${error.message}`);
        updateReviews(undefined);
      })
      .finally(() => {
        !isBackgroundProcess && updateReviewsStatus(false);
      });
  };

  /**
   * Handle rating change
   */
  useEffect(() => {
    if (rating) {
      clearReviewsData();
      requestNewPage(
        AppConstants.API_PAGINATION_BASE,
        false,
        setCurrentPage,
      )?.then(() => {
        requestNewPage(AppConstants.API_PAGINATION_BASE + 1, true, setNextPage);
      });
    }
  }, [rating]);

  /**
   * Handle sort change
   */
  useEffect(() => {
    if (typeof reverse !== undefined && pageData && !isLoadingReviews) {
      let { totalPages } = pageData;
      const newPageNumber = reverse
        ? totalPages
        : AppConstants.API_PAGINATION_BASE;
      if (newPageNumber) {
        clearReviewsData();
        requestNewPage(newPageNumber, false, setCurrentPage)?.then(() => {
          if (reverse) {
            if (newPageNumber - 1 > AppConstants.API_PAGINATION_BASE) {
              requestNewPage(newPageNumber - 1, true, setPreviousPage);
            }
          } else {
            if (newPageNumber + 1 < totalPages) {
              requestNewPage(newPageNumber + 1, true, setNextPage);
            }
          }
        });
      }
    }
  }, [reverse]);

  /**
   * Handle prev or next offset
   */
  useEffect(() => {
    if (currentPage && !isLoadingReviews) {
      updateReviewsStatus(true);
      clearReviewsData(false);
      const isNextAction = pageNumber > currentPage?.page.number;
      const newPage = isNextAction ? nextPage : previousPage;
      const newPageSetting = isNextAction ? setNextPage : setPreviousPage;
      const { totalPages } = currentPage.page;

      if (newPage && newPage.page.number === pageNumber) {
        let { number, totalPages } = newPage.page;
        if (isNextAction) {
          setPreviousPage(currentPage);
        } else {
          setNextPage(currentPage);
        }
        setCurrentPage(newPage);
        const nextPageRequest = validatePage(number, totalPages, !isNextAction);
        if (nextPageRequest) {
          requestNewPage(nextPageRequest, true, newPageSetting);
        }
        updateReviewsStatus(false);
      } else if (pageNumber <= totalPages) {
        requestNewPage(pageNumber, false, setCurrentPage)?.then(() => {
          const nextPageRequest = validatePage(
            pageNumber,
            totalPages,
            !isNextAction,
          );
          if (nextPageRequest) {
            requestNewPage(nextPageRequest, true, newPageSetting);
          }
        });
      }
    }
  }, [pageNumber]);

  useEffect(() => {
    /**
     * Update initial next page
     */

    if (reviewsModel) {
      const newPageData = {
        ...reviewsModel.page,
        number: AppConstants.API_PAGINATION_BASE as number,
      };
      setPageData(newPageData);
      setCurrentPage({ ...reviewsModel, page: newPageData });
      let { number, totalPages } = newPageData;
      const nextPageRequest = validatePage(number, totalPages, false);
      if (nextPageRequest) {
        requestNewPage(nextPageRequest, true, setNextPage);
      }
      updateReviewsStatus(false);
    }

    // Tracking
    onHandleReviewsReloadTrackingEvent(
      rating || 'all',
      reverse || false,
      reverse !== undefined,
      pageData?.totalElements || 0,
      trackingManager,
      pageData && reviewsModel
        ? { ...reviewsModel, page: pageData }
        : undefined,
    );
    return () => {
      clearReviewsData(false);
    };
  }, []);

  const onHandleChangeTracking = (state: boolean) => {
    onHandleCollapseTextEventTracking(
      state ? 'view more' : 'view less',
      trackingManager,
      reviewsModel,
    );
  };

  return (
    <>
      {!isLoadingReviews ? (
        <>
          {currentPage && currentPage.reviews?.length ? (
            <>
              <ReviewsContainer>
                {currentPage.reviews.map((review: Review) => (
                  <ReviewExtendedCard
                    review={review}
                    key={review.id}
                    onHandleChangeTracking={onHandleChangeTracking}
                  />
                ))}
              </ReviewsContainer>
              <PageBar
                totalPages={pageData?.totalPages || 0}
                pageNumber={(pageData && pageData?.number) || 0}
                setPage={handlePageChange}
              />
            </>
          ) : (
            <NoReviews onReset={handleResetFilters} />
          )}
        </>
      ) : (
        <LoaderSpinner />
      )}
    </>
  );
};

export default ReviewsHandler;
